import {
  CONTACT_ORDER_DIGITAL_CHANNELS,
  type ContactOrderAppointmentRow,
  type ContactOrderDigitalChannel,
  type ContactOrderDigitalPlan,
  type RowCommunicationSelection,
} from "../../../../../_logic/contact-orders-contract"
import {
  buildPostalToggleOptionsForContactOrderRow,
  manualDigitalPlanForContactOrderRow,
} from "../../../../../_logic/channels"
import {
  communicationSelectionForPlanningRow,
  type ContactOrderSelectionState,
} from "../../../../../_logic/selection-state"
import {
  isKeepPreconfigurationChoice,
  type PreconfigurationChoice,
} from "../../../../../_logic/preconfiguration-keep"

export type ContactOrderDigitalPreset = "auto" | "disabled" | "custom"
export type ContactOrderDigitalChannelChange = PreconfigurationChoice<"enable" | "disable">
export type ContactOrderDigitalChannelChanges = Record<
  ContactOrderDigitalChannel,
  ContactOrderDigitalChannelChange
>
export type ContactOrderPreconfigurationSkipReason =
  | "email_unavailable"
  | "mobile_message_unavailable"
  | "phone_unavailable"
  | "digital_unavailable"
  | "postal_unavailable"
export type ContactOrderPreconfigurationReasonCounts = Partial<
  Record<ContactOrderPreconfigurationSkipReason, number>
>

export const createContactOrderDigitalChannelChanges = (): ContactOrderDigitalChannelChanges => ({
  EMAIL: "__keep__",
  MOBILE_MESSAGE: "__keep__",
  PHONE: "__keep__",
})

const channelUnavailableReason: Record<
  ContactOrderDigitalChannel,
  ContactOrderPreconfigurationSkipReason
> = {
  EMAIL: "email_unavailable",
  MOBILE_MESSAGE: "mobile_message_unavailable",
  PHONE: "phone_unavailable",
}
export type ContactOrderPostalPreset =
  | "priority_manual"
  | "priority_automatic"
  | "impersonal"
  | "notice"
  | "disabled"

export type ContactOrderPreconfigurationResult = {
  state: ContactOrderSelectionState
  appliedRows: ContactOrderAppointmentRow[]
  applied: number
  skipped: number
  skippedReasons: ContactOrderPreconfigurationReasonCounts
}

const applyDigitalChannelChanges = (
  row: ContactOrderAppointmentRow,
  current: ContactOrderDigitalPlan,
  changes: ContactOrderDigitalChannelChanges,
  allowedContactChannels: readonly string[],
):
  | { digitalByCustomerId: ContactOrderDigitalPlan; reason?: never }
  | { digitalByCustomerId?: never; reason: ContactOrderPreconfigurationSkipReason } => {
  const next = { ...current }
  for (const channel of CONTACT_ORDER_DIGITAL_CHANNELS) {
    const change = changes[channel]

    if (isKeepPreconfigurationChoice(change)) continue

    if (change === "disable") {
      for (const [customerId, channels] of Object.entries(next)) {
        const remaining = channels.filter((entry) => entry !== channel)

        if (remaining.length) next[customerId] = remaining
        else delete next[customerId]
      }

      continue
    }

    // Already selected recipients keep their channel. Enabling must not add another recipient.
    if (Object.values(next).some((channels) => channels.includes(channel))) continue

    const candidates = Array.isArray(row.planningPreview?.digital_channel_tags)
      ? manualDigitalPlanForContactOrderRow(
          row,
          row.recipients.map((candidate) => candidate.customerId),
          [channel],
          allowedContactChannels,
        )
      : {}
    const recipient = row.recipients.find((candidate) =>
      candidates[candidate.customerId]?.includes(channel),
    )

    if (!recipient) return { reason: channelUnavailableReason[channel] }

    next[recipient.customerId] = [...(next[recipient.customerId] ?? []), channel]
  }

  return { digitalByCustomerId: next }
}

const postalSelectionFromPreset = (
  row: ContactOrderAppointmentRow,
  preset: ContactOrderPostalPreset,
  allowedContactChannels: readonly string[],
): RowCommunicationSelection["postal"] | null => {
  if (preset === "disabled") return { mode: "DISABLED" }

  if (!allowedContactChannels.includes("LETTER")) return null

  if (!row.planningPreview) return null

  if (preset === "priority_automatic" && !row.planningPreview?.automatic_postal_available)
    return null

  const mode = preset === "impersonal" ? "IMPERSONAL" : preset === "notice" ? "NOTICE" : "PERSONAL"
  // Preview options already follow the shared recipient/address priority. Do not use the
  // current postal_selection: that may reflect an explicit individual choice or a disabled tag.
  const option = buildPostalToggleOptionsForContactOrderRow(row, allowedContactChannels).find(
    (candidate) => candidate.mode === mode && !candidate.disabled,
  )

  if (!option?.selection) return null

  return {
    ...option.selection,
    dispatch_mode: preset === "priority_automatic" ? "AUTOMATIC" : "MANUAL",
  }
}

export const applyContactOrderPreconfiguration = ({
  targetRows,
  digitalPreset,
  digitalChannelChanges,
  postalPreset,
  automaticSelectionsByEventId = {},
  state,
  allowedContactChannels,
}: {
  targetRows: ContactOrderAppointmentRow[]
  digitalPreset: PreconfigurationChoice<ContactOrderDigitalPreset>
  digitalChannelChanges: ContactOrderDigitalChannelChanges
  postalPreset: PreconfigurationChoice<ContactOrderPostalPreset>
  automaticSelectionsByEventId?: Record<string, RowCommunicationSelection>
  state: ContactOrderSelectionState
  allowedContactChannels: readonly string[]
}): ContactOrderPreconfigurationResult => {
  const nextSelections = { ...state.communicationSelectionByEventId }
  const appliedRows: ContactOrderAppointmentRow[] = []
  const skippedReasons: ContactOrderPreconfigurationReasonCounts = {}
  let applied = 0
  let skipped = 0
  const keepDigital =
    isKeepPreconfigurationChoice(digitalPreset) ||
    (digitalPreset === "custom" &&
      CONTACT_ORDER_DIGITAL_CHANNELS.every((channel) =>
        isKeepPreconfigurationChoice(digitalChannelChanges[channel]),
      ))

  if (keepDigital && isKeepPreconfigurationChoice(postalPreset)) {
    return { state, appliedRows, applied: targetRows.length, skipped, skippedReasons }
  }

  for (const row of targetRows) {
    const rowKey = String(row.id)
    const current = communicationSelectionForPlanningRow(row, state, allowedContactChannels)
    const automatic = automaticSelectionsByEventId[rowKey]
    const customDigital =
      digitalPreset === "custom" && !keepDigital
        ? applyDigitalChannelChanges(
            row,
            current.digitalByCustomerId,
            digitalChannelChanges,
            allowedContactChannels,
          )
        : null
    const digitalByCustomerId = keepDigital
      ? current.digitalByCustomerId
      : digitalPreset === "disabled"
        ? {}
        : digitalPreset === "custom"
          ? customDigital?.digitalByCustomerId
          : automatic?.digitalByCustomerId
    const postal = isKeepPreconfigurationChoice(postalPreset)
      ? current.postal
      : postalSelectionFromPreset(row, postalPreset, allowedContactChannels)

    if (
      !postal ||
      !digitalByCustomerId ||
      (digitalPreset === "auto" &&
        !Object.values(digitalByCustomerId).some((channels) => channels.length))
    ) {
      skipped += 1
      const reason =
        customDigital?.reason ??
        (!digitalByCustomerId ||
        (digitalPreset === "auto" &&
          !Object.values(digitalByCustomerId).some((channels) => channels.length))
          ? "digital_unavailable"
          : "postal_unavailable")
      skippedReasons[reason] = (skippedReasons[reason] ?? 0) + 1
      continue
    }

    const next = { digitalByCustomerId, postal }
    const previous = state.communicationSelectionByEventId[rowKey] ?? current

    if (JSON.stringify(next) !== JSON.stringify(previous)) {
      nextSelections[rowKey] = next
      appliedRows.push(row)
    }

    applied += 1
  }

  return {
    state: appliedRows.length ? { communicationSelectionByEventId: nextSelections } : state,
    appliedRows,
    applied,
    skipped,
    skippedReasons,
  }
}
